import { Alert, Box, Button, ButtonRow, FormGroup, Heading, Select, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPatient } from '@common/interfaces/patient';
import type { IOption } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums';
import { TicketType } from '@common/enums/ticketType';
import { getDisplayName } from '@common/utils/getDisplayName';
import { patientAccessReasonSchema } from '@common/validation/patientAccessReason';
import { Loading } from '@frontend/components/elements/common';
import { TypeaheadInput } from '@frontend/components/fields';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { AbortableFetch } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, getValidationErrorTranslation } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';

interface IPatientAccessReasonsProps {
    children?: JSX.Element;
    onCancel: () => void;
    onReasonGiven: () => void;
    patientId?: string;
    requestId?: string;
}

interface IPatientAccessReasons {
    reasonForAccess: string | null;
    ticketInformation: string | null;
    ticketType: TicketType | null;
}

export function PatientAccessReasons({
    children,
    onCancel,
    onReasonGiven,
    patientId,
    requestId,
}: IPatientAccessReasonsProps) {
    const translate = getSelectedLanguage();

    const abortableFetch = new AbortableFetch();
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const [patient, setPatient] = useState<IPatient>();

    const [patientAccessReasons, setPatientAccessReasons] = useState<IPatientAccessReasons>({
        reasonForAccess: null,
        ticketInformation: null,
        ticketType: null,
    });

    const [isSaving, setIsSaving] = useState(false);
    const [isLimitExceeded, setIsLimitExceeded] = useState<boolean | undefined>(undefined);
    const [canReadWithoutReason, setCanReadWithoutReason] = useState<boolean | undefined>(undefined);
    const [errors, setErrors] = useState(emptyValidationErrors);

    if (isManager) {
        assertHasAuthorization(ManagerAuthorization.patientProcesses);
    }

    useEffect(() => {
        getIsLimitExceededForPatientAccess();

        return () => {
            abortableFetch.abort();
        };
    }, []);

    useEffect(() => {
        if (!isLimitExceeded) {
            if (patientId) {
                loadPatient();
            } else {
                setCanReadWithoutReason(true);
            }
        }
    }, [isLimitExceeded]);

    if (canReadWithoutReason) {
        return children || null;
    } else {
        return (
            <Loading
                item={canReadWithoutReason !== undefined}
                loaded={() => (
                    <PageTemplate>
                        <Box
                            display="flex"
                            flexDirection={'column'}
                            gap={8}
                            id="patient-access-reasons"
                        >
                            <Heading isSensitiveData={true}>{getDisplayName(patient ? patient : {})}</Heading>
                            {isLimitExceeded ? (
                                <Alert appearance="info">{translate.patientAccess.notPossibleToAccess}</Alert>
                            ) : (
                                <>
                                    <Box>
                                        <FormGroup
                                            hasErrors={Boolean(errors.get('ticketType'))}
                                            isRequired={true}
                                            label={translate.patientAccess.notification}
                                        >
                                            <Select
                                                className="automation-select"
                                                error={getValidationErrorTranslation(errors.get('ticketType'))}
                                                onChange={async (value) => {
                                                    const newValue = value?.value ?? null;
                                                    await handleChange('ticketType', newValue);
                                                    await validateOne('ticketType', newValue);
                                                }}
                                                options={getTicketTypeOptions()}
                                                placeholder={translate.patientAccess.chooseNotification}
                                                value={getTicketTypeValue(patientAccessReasons.ticketType)}
                                            />
                                        </FormGroup>

                                        {patientAccessReasons.ticketType !== null && (
                                            <FormGroup
                                                hasErrors={errors.has('ticketInformation')}
                                                isRequired={true}
                                                label={translate.patientAccess.notificationDetails}
                                            >
                                                <TextInput
                                                    className="automation-textinput"
                                                    error={getValidationErrorTranslation(
                                                        errors.get('ticketInformation')
                                                    )}
                                                    onBlur={async (event) => {
                                                        await validateOne('ticketInformation', event.target.value);
                                                    }}
                                                    onChange={async (event) => {
                                                        const newValue = event.target.value;
                                                        await handleChange('ticketInformation', newValue);

                                                        if (newValue !== '') {
                                                            await validateOne('ticketInformation', newValue);
                                                        }
                                                    }}
                                                    placeholder={`${
                                                        translate.ticketTypeEnum[patientAccessReasons.ticketType]
                                                    }...`}
                                                    value={patientAccessReasons.ticketInformation ?? ''}
                                                />
                                            </FormGroup>
                                        )}
                                        <FormGroup
                                            hasErrors={errors.has('reasonForAccess')}
                                            isRequired={true}
                                            label={translate.patientAccess.reasonForAccess}
                                        >
                                            <TypeaheadInput
                                                error={errors.get('reasonForAccess')}
                                                onChange={async (value) => await handleChange('reasonForAccess', value)}
                                                onDoValidate={async (value) =>
                                                    await validateOne('reasonForAccess', value || '')
                                                }
                                                options={getReasonForAccessDefaultOptions()}
                                                placeholder={translate.common.describeOrChooseFromList}
                                                value={patientAccessReasons.reasonForAccess}
                                                variant="outline"
                                            />
                                        </FormGroup>
                                    </Box>
                                    <ButtonRow>
                                        <Button
                                            onClick={onCancel}
                                            variant="outline"
                                        >
                                            {translate.common.cancel}
                                        </Button>
                                        <Button
                                            iconRight="caret-right-big"
                                            isDisabled={isSaving}
                                            onClick={async () => await onSubmit()}
                                            variant="solid"
                                        >
                                            {translate.common.save}
                                        </Button>
                                    </ButtonRow>
                                </>
                            )}
                        </Box>
                    </PageTemplate>
                )}
            />
        );
    }

    function getTicketTypeOptions(): IOption<TicketType>[] {
        return Object.values(TicketType).map((ticketType) => ({
            label: translate.ticketTypeEnum[ticketType],
            value: ticketType,
        }));
    }

    function getTicketTypeValue(value: string | null) {
        if (!value) {
            return;
        }

        return getTicketTypeOptions().find((entry) => entry.value === value);
    }

    function getReasonForAccessDefaultOptions() {
        return [
            translate.patientAccess.reasonForAccessDefaultOptions.uncertaintyResult,
            translate.patientAccess.reasonForAccessDefaultOptions.requestRhythmStripsRequestGP,
            translate.patientAccess.reasonForAccessDefaultOptions.requestRhythmStripsRequestMedicalSpecialist,
            translate.patientAccess.reasonForAccessDefaultOptions.checkRequest,
            translate.patientAccess.reasonForAccessDefaultOptions.viewTechnicalNote,
            translate.patientAccess.reasonForAccessDefaultOptions.internalImprovingService,
            translate.patientAccess.reasonForAccessDefaultOptions.failedExamination,
            translate.patientAccess.reasonForAccessDefaultOptions.patientmixUp,
        ];
    }

    async function handleChange<K extends keyof IPatientAccessReasons>(name: K, value: IPatientAccessReasons[K]) {
        setPatientAccessReasons({ ...patientAccessReasons, [name]: value });
    }

    async function validateOne(name: string, value: string | null) {
        const toValidatePatientAccessReasonObject = { ...patientAccessReasonSchema, [name]: value };

        setErrors(
            updateValidationErrorsForOne(patientAccessReasonSchema, toValidatePatientAccessReasonObject, errors, name)
        );
    }

    async function loadPatient() {
        const url = `/api/patients/${patientId}?includeItemAuthorizations=canReadWithoutReason`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setPatient(result.json);
        setCanReadWithoutReason(result.json.authorization.canReadWithoutReason);
    }

    async function getIsLimitExceededForPatientAccess() {
        const result = await abortableFetch.fetchJson('/api/patientAccessReasons/limit', 'GET');
        setIsLimitExceeded(result.json.limitIsExceeded);
    }

    async function onSubmit() {
        const validationResult = validate(patientAccessReasonSchema, {
            patientId: patientId,
            reasonForAccess: patientAccessReasons.reasonForAccess,
            requestId: requestId,
            ticketInformation: patientAccessReasons.ticketInformation,
            ticketType: patientAccessReasons.ticketType,
        });
        setErrors(validationResult.errors);

        if (validationResult.errors.size > 0) {
            return;
        }

        setIsSaving(true);
        const url = '/api/patientAccessReasons';
        await abortableFetch.fetchJson(url, 'POST', validationResult.item);
        await loadPatient();
        onReasonGiven();
    }
}
